import { LoginRequestBody, LoginResponse, ChangePasswordRequestBody, ChangePasswordResponse, RefreshResponse, LogoutResponse, ActivateAccountResponse, UserProfile, UpdateProfileBody, UpdateProfileResponse } from './types';
import { API_BASE_URL } from './config';
import { validateAccessTokenOnSet } from './security';
import { tokenStorage, ACCESS_KEY, REFRESH_KEY } from './storage';
import { authEmitter } from './emitter';

async function requestJson<T>(path: string, method: string, body?: any, access?: string): Promise<T> {
  const headers: Record<string,string> = {};
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (access) headers['Authorization'] = `Bearer ${access}`;
  let res: Response;
  try {
    res = await fetch(`${API_BASE_URL}${path}`, {
      method,
      headers,
      body: body !== undefined ? JSON.stringify(body) : undefined,
    });
  } catch (e: any) {
    const err: any = new Error('Network error: Could not connect to API server. Check your internet connection or VPN.');
    err.cause = e;
    throw err;
  }
  if (!res.ok) {
    const txt = await res.text();
    let data: any; try { data = JSON.parse(txt); } catch { data = { detail: txt }; }
    // Extract detail array or string for cleaner display. Fall back to the
    // first field error (e.g. {"new_password": ["..."]}) so validation
    // messages surface instead of a generic HTTP code.
    let message = data.detail;
    if (Array.isArray(message)) message = message.join(', ');
    if (!message || typeof message !== 'string') {
      const firstField = data && typeof data === 'object'
        ? Object.values(data).find((v) => Array.isArray(v) || typeof v === 'string')
        : undefined;
      if (Array.isArray(firstField)) message = firstField.join(', ');
      else if (typeof firstField === 'string') message = firstField;
    }
    if (!message || typeof message !== 'string') message = `Request failed (HTTP ${res.status})`;
    const err: any = new Error(message);
    err.status = res.status;
    err.data = data;
    throw err;
  }
  return res.json();
}

async function postJson<T>(path: string, body: any, access?: string): Promise<T> {
  return requestJson<T>(path, 'POST', body, access);
}

class AuthServiceImpl {
  private access: string | null = null;
  private refresh: string | null = null;
  private refreshingPromise: Promise<string | null> | null = null;

  // Track which environment tokens were issued for
  private static ENV_KEY = 'auth.env';

  constructor(){ /* lazy load tokens */ }

  private getEnvFingerprint(): string {
    // Create a fingerprint based on proxy target / API URL
    const proxyTarget = process.env.REACT_APP_PROXY_TARGET || '';
    const apiBase = process.env.REACT_APP_API_BASE_URL || '';
    return `${proxyTarget}|${apiBase}`;
  }

  async init() {
    try {
      // Check if environment changed - if so, clear stale tokens
      const currentEnv = this.getEnvFingerprint();
      const storedEnv = localStorage.getItem(AuthServiceImpl.ENV_KEY);
      
      if (storedEnv && storedEnv !== currentEnv) {
        console.warn('[AuthService] Environment changed, clearing stale tokens');
        await tokenStorage.removeItem(ACCESS_KEY);
        await tokenStorage.removeItem(REFRESH_KEY);
        localStorage.setItem(AuthServiceImpl.ENV_KEY, currentEnv);
        this.access = null;
        this.refresh = null;
        authEmitter.emit({ type: 'tokens', access: null, refresh: null });
        return;
      }
      
      // Store current environment
      localStorage.setItem(AuthServiceImpl.ENV_KEY, currentEnv);

      this.access = await tokenStorage.getItem(ACCESS_KEY);
      this.refresh = await tokenStorage.getItem(REFRESH_KEY);
      
      // Validate tokens aren't corrupted - basic check
      if (this.access && (this.access.split('.').length !== 3)) {
        console.warn('[AuthService] Invalid access token format, clearing');
        this.access = null;
        await tokenStorage.removeItem(ACCESS_KEY);
      }
      if (this.refresh && (this.refresh.split('.').length !== 3)) {
        console.warn('[AuthService] Invalid refresh token format, clearing');
        this.refresh = null;
        await tokenStorage.removeItem(REFRESH_KEY);
      }
      
      authEmitter.emit({ type: 'tokens', access: this.access, refresh: this.refresh });
    } catch (e) {
      console.error('[AuthService] Init failed, clearing tokens:', e);
      this.access = null;
      this.refresh = null;
      try { await tokenStorage.removeItem(ACCESS_KEY); } catch {}
      try { await tokenStorage.removeItem(REFRESH_KEY); } catch {}
      authEmitter.emit({ type: 'tokens', access: null, refresh: null });
    }
  }

  getAccess(){ return this.access; }
  getRefresh(){ return this.refresh; }

  private async persist(access: string | null, refresh: string | null) {
    this.access = access;
    this.refresh = refresh;
    if (access) await tokenStorage.setItem(ACCESS_KEY, access); else await tokenStorage.removeItem(ACCESS_KEY);
    if (refresh) await tokenStorage.setItem(REFRESH_KEY, refresh); else await tokenStorage.removeItem(REFRESH_KEY);
    authEmitter.emit({ type: 'tokens', access, refresh });
  }

  async login(body: LoginRequestBody) {
    const data = await postJson<LoginResponse>('/api/auth/login', body);
    const isAdmin = !!(data.is_staff || data.is_superuser);
    await this.persist(data.access, data.refresh);
    validateAccessTokenOnSet(data.access);
    authEmitter.emit({
      type: 'login',
      state: {
        user: {
          id: data.user_id,
          username: data.username,
          is_staff: data.is_staff,
          is_superuser: data.is_superuser,
        },
        access: data.access,
        refresh: data.refresh,
        needsPasswordChange: data.force_password_change,
        loading: false,
        error: null,
        isAdmin,
      }
    });
    return data;
  }

  async changePassword(body: ChangePasswordRequestBody) {
    if (!this.access) throw new Error('No access token');
    const data = await postJson<ChangePasswordResponse>('/api/auth/change-password', body, this.access);
    const newAccess = data.access;
    const newRefresh = data.refresh || this.refresh; // rotation maybe
    await this.persist(newAccess, newRefresh || null);
    validateAccessTokenOnSet(newAccess);
    authEmitter.emit({ type: 'passwordChanged' });
    return data;
  }

  async getMe(): Promise<UserProfile> {
    if (!this.access) throw new Error('No access token');
    return requestJson<UserProfile>('/api/auth/me/', 'GET', undefined, this.access);
  }

  async updateMe(body: UpdateProfileBody): Promise<UpdateProfileResponse> {
    if (!this.access) throw new Error('No access token');
    const data = await requestJson<UpdateProfileResponse>('/api/auth/me/', 'PATCH', body, this.access);
    // A password change rotates tokens server-side; persist them so the
    // session stays valid without a re-login.
    if (data.access) {
      await this.persist(data.access, data.refresh || this.refresh);
      validateAccessTokenOnSet(data.access);
      authEmitter.emit({ type: 'passwordChanged' });
    }
    return data;
  }

  async activateAccount(body: { uid: string; token: string; new_password: string }) {
    const data = await postJson<ActivateAccountResponse>('/api/auth/activate-account/', body);
    const isAdmin = !!(data.is_staff || data.is_superuser);
    await this.persist(data.access, data.refresh);
    validateAccessTokenOnSet(data.access);
    const user = {
      id: data.id,
      username: data.username,
      is_staff: data.is_staff,
      is_superuser: data.is_superuser,
    };
    try {
      localStorage.setItem('auth.user', JSON.stringify(user));
    } catch {}
    authEmitter.emit({
      type: 'login',
      state: {
        user,
        access: data.access,
        refresh: data.refresh,
        needsPasswordChange: false,
        loading: false,
        error: null,
        isAdmin,
      }
    });
    return data;
  }

  async refreshAccess(): Promise<string | null> {
    if (this.refreshingPromise) return this.refreshingPromise; // queue
    if (!this.refresh) return null;
    this.refreshingPromise = (async () => {
      try {
        const data = await postJson<RefreshResponse>('/api/auth/token/refresh', { refresh: this.refresh });
        const newAccess = data.access;
        const newRefresh = data.refresh || this.refresh; // rotation if provided
        await this.persist(newAccess, newRefresh);
        validateAccessTokenOnSet(newAccess);
        return newAccess;
      } catch (e: any) {
        // If refresh fails (401/blacklisted), clear tokens
        if (e.status === 401) {
          await this.persist(null, null);
          authEmitter.emit({ type: 'logout' });
        }
        throw e;
      } finally {
        this.refreshingPromise = null;
      }
    })();
    return this.refreshingPromise;
  }

  async logout(): Promise<LogoutResponse> {
    if (!this.refresh) {
      await this.persist(null, null);
      authEmitter.emit({ type: 'logout' });
      return { detail: 'Already logged out' };
    }
    let data: LogoutResponse;
    try {
      data = await postJson<LogoutResponse>('/api/auth/logout', { refresh: this.refresh }, this.access || undefined);
    } catch (e: any) {
      // swallow non-200 for logout, still clear local state
      data = { detail: e.message || 'Logout error' };
    }
    await this.persist(null, null);
    authEmitter.emit({ type: 'logout' });
    return data;
  }
}

export const AuthService = new AuthServiceImpl();
